import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";

import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import { CustomOrganisationPrimaryRole, LeadEntityType } from "lib/types/graphQLEnums";
import { useCreateCustomOrganisation } from "../../../lib/hooks/api/customOrganisations/useCreateCustomOrganisation";
import { useCreateLead } from "../../../lib/hooks/api/leads/useCreateLead";
import {
  DocumentFindLeadsMetadata,
  documentFindLeadsMetadataToLeadFields,
} from "../../../lib/hooks/api/leads/useCreateLeadOneClick";
import { routes } from "../../../lib/routes";
import { EventData } from "../../../lib/tracking";
import { Flex } from "../../../styles/utility-components";
import { ToastAction } from "../../entities/ToastAction";
import { isBuyerOrganisation } from "../../entities/utils";
import CompanyUserSelect from "../../form_components/CompanyUserSelect";
import { DatePicker } from "../../form_components/Inputs";
import { BuyersCombobox } from "../../ui/form_components/BidEntityCombobox/BuyersCombobox";
import Input, { StyledInput } from "../../ui/input/Input";
import Label from "../../ui/input/Label";
import { LeadStageDropdownSelect } from "../lead_stage/LeadStageDropdownSelect";
import { CreateLeadFormInput, CreateLeadFormOutput, createLeadSchema } from "./utils";

type Props = {
  trackingData: EventData;
  defaultValues?: Partial<CreateLeadFormOutput>;
  hiddenFields?: (keyof CreateLeadFormOutput)[];
  onComplete?: (leadId?: string) => void;
  navigateToNewLead?: boolean;
  findLeadsMetadata?: DocumentFindLeadsMetadata;
};

export function CreateLeadForm({
  trackingData,
  defaultValues,
  hiddenFields,
  onComplete,
  navigateToNewLead,
  findLeadsMetadata,
}: Props) {
  const navigate = useNavigate();

  const defaultBuyerIds = useMemo(
    () => defaultValues?.entities?.filter(isBuyerOrganisation)?.map((e) => e.entityId) ?? [],
    [defaultValues?.entities],
  );

  const { formState, control, handleSubmit, reset, setValue, getValues } = useForm<
    CreateLeadFormInput,
    unknown,
    CreateLeadFormOutput
  >({
    defaultValues: {
      ...defaultValues,
      buyerIds: defaultBuyerIds,
    },
    resolver: zodResolver(createLeadSchema),
  });

  // Metadata lookup to enrich custom buyer IDs with their names
  // in the combobox and their entity type at submit time
  const [customBuyers, setCustomBuyers] = useState<{ id: string; name: string }[]>([]);

  const { mutate: createLead, isLoading } = useCreateLead(trackingData, {
    onSuccess: (data) => {
      reset();
      setCustomBuyers([]);
      if (navigateToNewLead) {
        void navigate({ to: `/leads/${data.createLead.id}` });
        return;
      }
      onComplete?.(data.createLead.id);
      toast.message("Lead created successfully", {
        action: <ToastAction objectType="lead" url={`${routes.leads}/${data.createLead.id}`} />,
      });
    },
    onError: () => toast.error("Failed to create lead"),
  });

  const { mutate: createCustomOrganisation } = useCreateCustomOrganisation({
    onSuccess: (data) => {
      const newCustomBuyer = data.createCustomOrganisation;
      const currentBuyerIds = getValues("buyerIds");
      setValue("buyerIds", [...(currentBuyerIds ?? []), newCustomBuyer.id], {
        shouldValidate: true,
      });
      setCustomBuyers((prev) => [...prev, newCustomBuyer]);
    },
    onError: (error) => {
      toast.error("Failed to add custom buyer");
      console.error("Failed to create custom buyer:", error);
    },
  });

  return (
    <Form
      onSubmit={handleSubmit(({ entities: _entities, buyerIds, ...lead }) => {
        const customBuyerIdSet = new Set(customBuyers.map((b) => b.id));

        const buyerEntities = (buyerIds ?? []).map((id) => ({
          entityId: id,
          entityType: customBuyerIdSet.has(id)
            ? LeadEntityType.CustomOrganisation
            : LeadEntityType.Buyer,
        }));

        const nonBuyerEntities = (defaultValues?.entities ?? []).filter(
          (e) => !isBuyerOrganisation(e) && e.entityType !== LeadEntityType.CustomOrganisation,
        );

        createLead({
          lead: {
            ...lead,
            ...documentFindLeadsMetadataToLeadFields(findLeadsMetadata),
          },
          entities: { entities: [...nonBuyerEntities, ...buyerEntities] },
        });
      })}
    >
      <Flex flexDirection="column" gap={24}>
        <div>
          <Label htmlFor="lead-name" required>
            Name
          </Label>
          <Input
            name="name"
            control={control}
            id="lead-name"
            required
            placeholder="Name this lead"
          />
        </div>
        <div>
          <Label htmlFor="buyer-ids">Buyer</Label>
          <BuyersCombobox
            control={control}
            name="buyerIds"
            trigger={<StyledInput id="buyer-ids" placeholder="Select buyers" />}
            customBuyers={customBuyers}
            onAddCustomBuyer={(name) =>
              createCustomOrganisation({
                input: {
                  name,
                  primaryRole: CustomOrganisationPrimaryRole.Buyer,
                },
              })
            }
          />
        </div>
        <CompanyUserSelect name="assignedToId" control={control} label="Assigned To" />
        {!hiddenFields?.includes("value") && (
          <div>
            <Label htmlFor="lead-value">Value</Label>
            <Input
              name="value"
              id="lead-value"
              control={control}
              type="number"
              placeholder="Add a value"
            />
          </div>
        )}

        <DatePicker
          name="expiryDate"
          control={control}
          label="Expiry Date"
          placeholder="Add an expiry date"
        />

        <LeadStageDropdownSelect
          name="stageId"
          control={control}
          label="Stage"
          placeholder="Select a stage"
          style={{ height: 36 }}
          required
        />
      </Flex>

      <Flex column gap={8} flexGrow={1} justifyContent="flex-end">
        <Button
          buttonType="primary"
          type="submit"
          disabled={!formState.isValid}
          loading={isLoading}
        >
          Create lead
        </Button>
      </Flex>
    </Form>
  );
}

const Form = styled.form({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  flexGrow: 1,
  gap: 24,
});
